import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  Grid,
  Chip,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TablePagination from "@mui/material/TablePagination";
import "./uwin.css";
import { uwinAPI } from "../../../services/uwin.api";
import Loader from "../../../components/Loader";
import { toast } from "react-toastify";
import { safeDecrypt } from "./uwin-decrypt";
import { getUwinClientToken, saveBlobToFile } from "../../../helpers/common-uwinhelper";

export default function DueList() {
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [sessionSite, setSessionSite] = useState(null);
  const [sessionSites, setSessionSites] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [activeFilter, setActiveFilter] = useState("week");
  const [rows, setRows] = useState([]);
  const [showClearButton, setShowClearButton] = useState(false);

  // Filter Buttons Configuration
  const filterButtons = [
    { label: "Next Week", value: "week" },
    { label: "Next 15 Days", value: "15days" },
    { label: "Next One Month", value: "month" },
    { label: dayjs().add(1, "month").format("MMMM YYYY"), value: "month1" },
    { label: dayjs().add(2, "month").format("MMMM YYYY"), value: "month2" },
    { label: dayjs().add(3, "month").format("MMMM YYYY"), value: "month3" },
  ];

  const handleFilterClick = (value) => {
    setActiveFilter(value);
    setShowClearButton(false);
    let start = dayjs();
    let end = dayjs();

    if (value === "week") {
      end = start.add(7, "day");
    } else if (value === "15days") {
      end = start.add(15, "day");
    } else if (value === "month") {
      end = start.add(30, "day");
    } else if (value === "month1") {
      start = dayjs().add(1, "month").startOf("month");
      end = start.endOf("month");
    } else if (value === "month2") {
      start = dayjs().add(2, "month").startOf("month");
      end = start.endOf("month");
    } else if (value === "month3") {
      start = dayjs().add(3, "month").startOf("month");
      end = start.endOf("month");
    }

    setFromDate(start);
    setToDate(end);
    getAshaDueLists(sessionSite, start, end);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    getAllAshaSessions();
  }, []);

  const getAllAshaSessions = async () => {
    try {
      setIsPageLoading(true);
      await getUwinClientToken();
      const response = await uwinAPI.getAllAshaSessionSites();

      if (response?.success) {
        const data = response?.data || [];
        setSessionSites(data);
        if (data.length > 0) {
          const firstSite = data[0];
          setSessionSite(firstSite);

          // Trigger "Next Week" on load (fields remain empty until user interaction)
          const start = dayjs();
          const end = dayjs().add(7, "day");
          getAshaDueLists(firstSite, start, end);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message?.error || "Failed to fetch session sites");
    } finally {
      setIsPageLoading(false);
    }
  };

  const getAshaDueLists = async (site, start, end) => {
    if (!site || !start || !end) return;
    try {
      setIsTableLoading(true);
      const payload = {
        from_date: start.format("YYYY-MM-DD"),
        to_date: end.format("YYYY-MM-DD"),
        session_site_id: site?.session_site_id,
        session_site_name: site?.session_site_name,
      };
      const response = await uwinAPI.ashaDueLists(payload);
      if (response?.success) {
        setRows(response?.data || []);
      } else {
        setRows([]);
      }
    } catch (error) {
      setRows([]);
      toast.error(error?.response?.data?.message?.error || "Failed to fetch due list");
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!sessionSite) {
      toast.error("Please select session site");
      return;
    }
    try {
      setIsPageLoading(true);
      const start = fromDate || dayjs();
      const end = toDate || dayjs().add(7, "day");

      const payload = {
        from_date: start.format("YYYY-MM-DD"),
        to_date: end.format("YYYY-MM-DD"),
        session_site_id: sessionSite?.session_site_id,
        session_site_name: sessionSite?.session_site_name,
      };

      const blob = await uwinAPI.ashaDueListsDownload(payload);
      const fileName = `DueList_${sessionSite?.session_site_name.replace(/\s+/g, "_")}_${dayjs().format("DD-MM-YYYY")}`;
      const success = saveBlobToFile(blob, fileName, "xlsx");

      if (success) {
        toast.success("Due list downloaded successfully!");
      } else {
        toast.error("Failed to download due list");
      }
    } catch (error) {
      toast.error("An error occurred during download");
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleCertificateDownload = (benRefId, benName) => async () => {
    if (!benRefId) {
      toast.error("Beneficiary reference ID not found");
      return;
    }
    try {
      setIsPageLoading(true);
      const payload = { beneficiary_ref_id: benRefId };
      const blob = await uwinAPI.downloadMcpCardAcknowledgement(payload);
      const fileName = `Certificate_${benName}`;
      const success = saveBlobToFile(blob, fileName, "pdf");

      if (success) {
        toast.success("Certificate downloaded successfully!");
      } else {
        toast.error("Failed to download certificate");
      }
    } catch (error) {
      toast.error("An error occurred during certificate download");
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleClear = () => {
    const start = dayjs();
    const end = dayjs().add(7, "day");
    setFromDate(null);
    setToDate(null);
    setActiveFilter("week");
    setShowClearButton(false);
    getAshaDueLists(sessionSite, start, end);
  };

  return (
    <>
      {isPageLoading && <Loader />}

      <Box p={3}>
        {/* Header with Icon */}
        <Stack direction="row" alignItems="center" spacing={1} mb={3}>
          <img src={"/images/uwin.svg"} alt="uwin" style={{ width: 50 }} />
          <Typography variant="h5" fontWeight={600}>
            Due List
          </Typography>
        </Stack>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3} sx={{ alignItems: "flex-start" }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="session-site-label">Session Sites</InputLabel>
                <Select
                  labelId="session-site-label"
                  value={sessionSite?.session_site_id || ""}
                  label="Session Sites"
                  onChange={(e) => {
                    const selectedSite = sessionSites.find(
                      (s) => s.session_site_id === e.target.value
                    );
                    setSessionSite(selectedSite);
                    getAshaDueLists(selectedSite, fromDate, toDate);
                  }}
                >
                  {sessionSites.map((site) => (
                    <MenuItem key={site?.session_site_id} value={site?.session_site_id}>
                      {site?.session_site_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  minDate={dayjs()}
                  maxDate={dayjs().add(1, "year")}
                  onChange={(newValue) => {
                    setFromDate(newValue);
                    setShowClearButton(true);
                    setActiveFilter("");
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    minDate={fromDate || dayjs("2016-01-01")}
                    maxDate={fromDate ? fromDate.add(1, "month") : dayjs("2039-12-31")}
                    onChange={(newValue) => {
                      setToDate(newValue);
                      getAshaDueLists(sessionSite, fromDate, newValue);
                    }}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
                {showClearButton && (
                  <Button variant="text" color="error" onClick={handleClear}>
                    Clear
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>

          <Stack spacing={2} mt={3}>
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "stretch" : "end"}
              flexWrap="wrap"
              gap={2}
            >
              {/* Left Side */}
              <Stack spacing={1}>
                <Typography fontWeight={500}>Filter By :</Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="start" width="100%">
                  {filterButtons.map((item) => (
                    <Button
                      key={item.value}
                      variant={
                        activeFilter === item.value ? "contained" : "outlined"
                      }
                      onClick={() => handleFilterClick(item.value)}
                      sx={{
                        flexGrow: isMobile ? 1 : 0,
                        minWidth: isMobile ? "48%" : "auto",
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Stack>
              </Stack>

              {/* Right Side Download */}
              {!isTableLoading &&
                <Button
                  startIcon={<DownloadIcon />}
                  variant="outlined"
                  onClick={handleDownload}
                  sx={{
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  Download
                </Button>
              }
            </Stack>
          </Stack>
        </Paper>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f1f1f4",
                    "& .MuiTableCell-root": {
                      fontWeight: 600,
                    },
                  }}
                >
                  <TableCell>Category</TableCell>
                  <TableCell>Beneficiary Name</TableCell>
                  <TableCell>DOB / Gender</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Mobile Number</TableCell>
                  <TableCell>Guardian Name</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isTableLoading ? (
                  [...Array(rowsPerPage)].map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell><Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 10 }} /></TableCell>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell><Skeleton variant="text" width={150} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    </TableRow>
                  ))
                ) : (
                  rows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow
                        key={row?.beneficiary_id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#f9f9f9" : "white",
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={row?.current_beneficiary_type_name}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>{row?.ben_name}</TableCell>
                        <TableCell>
                          {dayjs(row?.dob).format("DD/MM/YYYY")}, {row?.beneficiary_gender}
                        </TableCell>
                        <TableCell>
                          {row?.vaccines?.[0]?.due_date}
                        </TableCell>
                        <TableCell>{safeDecrypt(row?.ben_mobile_number)}</TableCell>
                        <TableCell>{row?.guardian_name}</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            onClick={handleCertificateDownload(row?.beneficiary_reference_id, row?.ben_name)}
                          >
                            <DownloadIcon />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box className="pagination-wrapper">
            <TablePagination
              sx={{
                "& p": {
                  marginBottom: 0,
                },
              }}
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rows?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Paper>
      </Box>
    </>
  );
}